"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Eye, Loader2, X } from "lucide-react";
import { useAuth } from "@/components/auth/AuthProvider";
import LandingView from "@/views/Landing";
import CreatorHomeView from "@/views/CreatorHome";
import type { Announcement } from "@/lib/landing/announcement";

/**
 * Decides which homepage to render at `/`:
 *
 *  - Signed-in subscribers (or admins) → CreatorHome (work-focused: nav,
 *    "welcome back" hero, goal cards, full tool grid, library).
 *  - Everyone else                      → Marketing landing (sales-focused).
 *
 * Admin preview: admins normally only ever see CreatorHome, so `/?preview=landing`
 * lets them inspect the marketing landing without logging out. A floating pill
 * marks preview mode and links back out.
 *
 * First paint (while client auth is still resolving): `hasAuthCookie` is read
 * server-side (presence only) so we can render the right shell immediately —
 * anonymous visitors get the SSR'd marketing landing (no blank page), while
 * likely-subscribers get a minimal loader to avoid a landing→CreatorHome flash.
 *
 * `announcement` is likewise resolved server-side and handed to every landing
 * render below (including the admin preview) so the bar is present in the first
 * paint instead of popping in and pushing the hero down.
 */
export function HomePageSwitcher({
  hasAuthCookie,
  announcement = null,
}: {
  hasAuthCookie?: boolean;
  announcement?: Announcement | null;
}) {
  const { user, billing, isAdmin, loading } = useAuth();
  const searchParams = useSearchParams();

  if (loading) {
    // No auth cookie → definitely anonymous → render the marketing landing now
    // (this is what gets SSR'd on first request). Otherwise show a loader while
    // the session resolves so a subscriber never flashes the landing first.
    if (!hasAuthCookie) return <LandingView announcement={announcement} />;
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" />
      </div>
    );
  }

  const previewLanding = isAdmin && searchParams.get("preview") === "landing";
  if (previewLanding) {
    return (
      <>
        <LandingView announcement={announcement} />
        <AdminPreviewPill />
      </>
    );
  }

  const hasPaidAccess =
    isAdmin ||
    !!billing.personalSubscriptionId ||
    (billing.personalSubscriptionIds?.length ?? 0) > 0;

  if (user && hasPaidAccess) {
    return <CreatorHomeView />;
  }

  return <LandingView announcement={announcement} />;
}

function AdminPreviewPill() {
  return (
    <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2">
      <div className="flex items-center gap-3 rounded-full border border-border/70 bg-background/95 px-4 py-2 text-sm shadow-lg backdrop-blur">
        <span className="flex items-center gap-2 font-medium text-foreground">
          <Eye className="h-4 w-4 text-accent" />
          Previewing landing page
        </span>
        <span className="h-4 w-px bg-border/70" aria-hidden />
        <Link
          href="/"
          className="inline-flex items-center gap-1 text-muted-foreground transition hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
          Exit
        </Link>
      </div>
    </div>
  );
}
